import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { FloppyDisk, Info } from '@phosphor-icons/react';
import { schoolConfigApi } from '../../lib/api';

/**
 * Settings tab: online-payment tuning. Currently exposes only the GeniusPay
 * amount coefficient — the multiplier applied to the base amount before the
 * request is sent to GeniusPay. Purpose: pass GeniusPay's platform fees on to
 * the paying parent so the school always receives the base amount net.
 *
 * Bounds enforced on backend: [1.0, 2.0]. UI clamps live to the same range.
 */
export default function PaymentSettingsTab() {
  const [coefficient, setCoefficient] = useState('1.00');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [persisted, setPersisted] = useState(1.0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await schoolConfigApi.getPaymentSettings();
      const v = Number(data?.geniuspay_amount_coefficient ?? 1.0);
      setPersisted(v);
      setCoefficient(v.toFixed(2));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const num = parseFloat((coefficient || '').replace(',', '.'));
    if (Number.isNaN(num) || num < 1.0 || num > 2.0) {
      toast.error('Coefficient doit être entre 1.00 et 2.00');
      return;
    }
    setSaving(true);
    try {
      const { data } = await schoolConfigApi.updatePaymentSettings({
        geniuspay_amount_coefficient: num,
      });
      const v = Number(data?.geniuspay_amount_coefficient ?? num);
      setPersisted(v);
      setCoefficient(v.toFixed(2));
      toast.success(`Coefficient enregistré : ${v.toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const dirty = parseFloat((coefficient || '').replace(',', '.')) !== persisted;

  const preview100k = () => {
    const num = parseFloat((coefficient || '').replace(',', '.')) || 1;
    return Math.round(100000 * num).toLocaleString('fr-FR');
  };

  if (loading) {
    return <p className="text-sm text-[#64748B]">Chargement…</p>;
  }

  return (
    <div className="space-y-4" data-testid="payment-settings-tab">
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader>
          <CardTitle className="font-outfit">Coefficient GeniusPay</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-[#0F172A] flex gap-2">
            <Info size={18} className="text-blue-600 shrink-0 mt-0.5" weight="duotone" />
            <div className="space-y-1">
              <p>
                Le coefficient multiplie le <b>montant de base à régler</b> avant l&apos;envoi vers
                GeniusPay. Le parent voit donc <b>montant + frais</b> sur l&apos;interface GeniusPay.
              </p>
              <p className="text-xs text-[#475569]">
                Bornes : entre <b>1.00</b> (pas de majoration) et <b>2.00</b> (double). Exemple à
                1.05 : le parent paie 5&nbsp;% de plus pour couvrir les frais de transaction.
              </p>
            </div>
          </div>

          <div className="max-w-xs space-y-2">
            <Label htmlFor="coef-input" className="text-sm font-semibold">
              Coefficient (1.00 → 2.00)
            </Label>
            <Input
              id="coef-input"
              type="number"
              step="0.01"
              min="1.00"
              max="2.00"
              value={coefficient}
              onChange={(e) => setCoefficient(e.target.value)}
              data-testid="geniuspay-coefficient-input"
              className="w-32"
            />
          </div>

          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
            <p className="text-xs text-[#64748B]">Simulation sur 100&nbsp;000 FCFA de solde :</p>
            <p className="font-outfit text-lg font-bold text-[#0F172A] mt-1" data-testid="coef-preview">
              → Envoyé à GeniusPay : <span className="text-emerald-700">{preview100k()} FCFA</span>
            </p>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-[#0052FF] hover:bg-[#0040D9]"
            data-testid="save-coefficient-btn"
          >
            <FloppyDisk size={16} className="mr-2" />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
